// =====================================================
// Swiss Finance OS - Receipt Viewer Component
// Modal viewer for receipt images and PDFs
// =====================================================

import React, { useState, useEffect } from 'react';
import { receiptService, type ReceiptUpload } from '@/services/receiptService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Trash2,
  Loader2,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  ZoomIn,
  ZoomOut,
  RotateCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ReceiptViewerProps {
  receipt: ReceiptUpload | null;
  open: boolean;
  onClose: () => void;
  onDelete?: () => void;
}

export const ReceiptViewer: React.FC<ReceiptViewerProps> = ({
  receipt,
  open,
  onClose,
  onDelete
}) => {
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    if (receipt && open) {
      loadReceipt();
    } else {
      setReceiptUrl(null);
      setIsLoading(true);
      setZoom(100);
      setRotation(0);
    }
  }, [receipt, open]);

  const loadReceipt = async () => {
    if (!receipt) return;

    setIsLoading(true);
    try {
      const url = await receiptService.getReceiptUrl(receipt.storage_path);
      setReceiptUrl(url);
    } catch (error) {
      console.error('Error loading receipt:', error);
      toast({
        title: 'Error loading receipt',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!receiptUrl || !receipt) return;

    try {
      const response = await fetch(receiptUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = receipt.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: `Downloading ${receipt.file_name}`
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async () => {
    if (!receipt) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${receipt.file_name}? This action cannot be undone.`
    );

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      const result = await receiptService.deleteReceipt(receipt.id);
      if (result.success) {
        toast({
          title: 'Receipt deleted',
          description: `${receipt.file_name} has been deleted`
        });
        onDelete?.();
        onClose();
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const getOcrStatusBadge = () => {
    if (!receipt) return null;

    const statusConfig = {
      pending: { label: 'OCR Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
      processed: { label: 'OCR Complete', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { label: 'OCR Failed', color: 'bg-red-100 text-red-800', icon: AlertTriangle }
    };

    const config = statusConfig[receipt.ocr_status];
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const renderContent = () => {
    if (!receipt) return null;

    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 text-brand-primary animate-spin" />
        </div>
      );
    }

    if (!receiptUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-center">
          <AlertTriangle className="h-12 w-12 text-orange-500 mb-4" />
          <p className="text-lg font-medium text-gray-900">Failed to load receipt</p>
          <p className="text-sm text-gray-500 mt-1">The receipt file could not be loaded</p>
        </div>
      );
    }

    const isPdf = receipt.mime_type === 'application/pdf';

    if (isPdf) {
      return (
        <div className="w-full h-[600px]">
          <iframe
            src={receiptUrl}
            className="w-full h-full border-0 rounded-lg"
            title={receipt.file_name}
          />
        </div>
      );
    }

    // Image viewer with zoom and rotation
    return (
      <div className="relative overflow-auto max-h-[600px] bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-center min-h-[500px]">
          <img
            src={receiptUrl}
            alt={receipt.file_name}
            className="max-w-full h-auto transition-transform duration-300"
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transformOrigin: 'center center'
            }}
          />
        </div>

        {/* Image Controls */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 50}
            className="rounded-full"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[50px] text-center">
            {zoom}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 200}
            className="rounded-full"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <div className="w-px h-6 bg-gray-300 mx-2" />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRotate}
            className="rounded-full"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (!receipt) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-brand-primary" />
              {receipt.file_name}
            </DialogTitle>
            {getOcrStatusBadge()}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
            <span>{(receipt.file_size / 1024).toFixed(1)} KB</span>
            <span>•</span>
            <span>{new Date(receipt.uploaded_at).toLocaleDateString()}</span>
            {receipt.ocr_confidence && (
              <>
                <span>•</span>
                <span>OCR Confidence: {receipt.ocr_confidence.toFixed(1)}%</span>
              </>
            )}
          </div>
        </DialogHeader>

        {/* Receipt Content */}
        <div className="mt-4">
          {renderContent()}
        </div>

        {/* Extracted Data (if available) */}
        {receipt.ocr_status === 'processed' && receipt.extracted_data && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-sm text-blue-900 mb-2">Extracted Data</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(receipt.extracted_data).map(([key, value]) => (
                <div key={key}>
                  <span className="font-medium text-gray-700">{key}: </span>
                  <span className="text-gray-600">{JSON.stringify(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validation Mismatches (if any) */}
        {receipt.has_mismatches && receipt.validation_results && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <h4 className="font-semibold text-sm text-orange-900">Validation Issues</h4>
            </div>
            <div className="text-sm text-gray-700">
              {JSON.stringify(receipt.validation_results, null, 2)}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={!receiptUrl}
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Delete
          </Button>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
